<template>
    <Button
        v-if="isEditing"
        class="view-button"
        icon="pi pi-eye"
        label="View"
        @click="stopEditing()"
        v-tooltip.left="{
            value: 'Return to view mode.',
            showDelay: 500,
        }"
    />
    <Button
        v-if="!isEditing"
        severity="secondary"
        icon="pi pi-pencil"
        :label="canEdit ? 'Edit' : `Syncing (${secondsLeft})`"
        :loading="!canEdit"
        @click="startEditing()"
        v-tooltip.left="{
            value: 'Start editing.',
            showDelay: 500,
        }"
    />
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import { useEditManager, useEditStore } from "@/composables/edit-manager";
import { INACTIVITY_TIMEOUT_MS } from "@/stores/sync-store";
import { syncChannel } from "@/lib/broadcast-channels";

const { canEdit, isEditing } = useEditManager();
const { startEditing, stopEditing } = useEditStore();
const secondsLeft = ref(0);
const interval = ref<NodeJS.Timeout>();

/**
 * Starts the countdown.
 */
const startCountdown = () => {
    secondsLeft.value = INACTIVITY_TIMEOUT_MS / 1000 + 1;
    interval.value = setInterval(() => {
        if (secondsLeft.value > 0) {
            secondsLeft.value -= 1;
        } else {
            clearInterval(interval.value);
        }
    }, 1000);
};

/**
 * (Re-)start the countdown on each new queue.
 */
syncChannel.addEventListener("message", ({ data: { message } }) => {
    if (message === "queued") {
        clearInterval(interval.value);
        startCountdown();
    }
});

/**
 * Clear countdown if you can edit again.
 */
watch(
    () => canEdit.value,
    (newValue) => {
        if (newValue) {
            clearInterval(interval.value);
        }
    }
);
</script>

<style lang="scss" scoped>
.view-button {
    background-color: var(--stekz-primary-background);
    border-color: var(--stekz-primary-background);
    color: var(--stekz-primary-color);
}
.p-button {
    white-space: nowrap;
}
</style>
